import { create } from 'zustand';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  exiting?: boolean;
}

const MAX_TOASTS = 5;

interface ToastState {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => string;
  removeToast: (id: string) => void;
  dismissToast: (id: string) => void;
  clearToasts: () => void;
}

const generateId = () => `toast_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const pendingTimers = new Map<string, ReturnType<typeof setTimeout>>();

export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],

  addToast: (newToast) => {
    const { toasts } = get();

    const isDuplicate = toasts.some(
      (t) =>
        !t.exiting &&
        t.type === newToast.type &&
        t.title === newToast.title &&
        t.message === newToast.message
    );
    if (isDuplicate) return '';

    const id = generateId();
    const duration = newToast.duration ?? 4000;

    let updated = [...toasts, { ...newToast, id, duration }];

    if (updated.length > MAX_TOASTS) {
      const removed = updated.slice(0, updated.length - MAX_TOASTS);
      removed.forEach((t) => {
        const timer = pendingTimers.get(t.id);
        if (timer) {
          clearTimeout(timer);
          pendingTimers.delete(t.id);
        }
      });
      updated = updated.slice(updated.length - MAX_TOASTS);
    }

    set({ toasts: updated });

    if (duration > 0) {
      const timer = setTimeout(() => {
        get().dismissToast(id);
        pendingTimers.delete(id);
      }, duration);
      pendingTimers.set(id, timer);
    }

    return id;
  },

  dismissToast: (id) => {
    set((state) => ({
      toasts: state.toasts.map((t) =>
        t.id === id ? { ...t, exiting: true } : t
      ),
    }));

    setTimeout(() => {
      get().removeToast(id);
    }, 300);
  },

  removeToast: (id) => {
    const timer = pendingTimers.get(id);
    if (timer) {
      clearTimeout(timer);
      pendingTimers.delete(id);
    }
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    }));
  },

  clearToasts: () => {
    pendingTimers.forEach((timer) => clearTimeout(timer));
    pendingTimers.clear();
    set({ toasts: [] });
  },
}));

export const toast = {
  success: (title: string, message?: string, duration?: number) => {
    return useToastStore.getState().addToast({ type: 'success', title, message, duration });
  },
  error: (title: string, message?: string, duration?: number) => {
    return useToastStore.getState().addToast({ type: 'error', title, message, duration });
  },
  warning: (title: string, message?: string, duration?: number) => {
    return useToastStore.getState().addToast({ type: 'warning', title, message, duration });
  },
  info: (title: string, message?: string, duration?: number) => {
    return useToastStore.getState().addToast({ type: 'info', title, message, duration });
  },
};
